/**
 * Component: SurfaceHost — in-place widget surface navigation
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R09, WIDGET-V1-R10, WIDGET-V1-R11, WIDGET-V1-R12,
 *               WIDGET-V1-R13, WIDGET-V1-R28, BROWSE-V1-R20, BROWSE-V1-R21
 *
 * The widget mounts a single surface (chat / a search surface /
 * browse) into the host page's container. The retrieval surfaces
 * render a cross-module "View in full guideline" link
 * (``BrowseLink``, AB#4396) on result + citation rows. Clicking it
 * must NOT open a new tab or navigate the host page away — it
 * stays inside the host page (the customer's site, with its own
 * header/footer chrome) and just swaps the mounted surface to the
 * Browse viewer, deep-linked to the cited guideline + section.
 *
 * ``SurfaceHost`` is the wrapper ``mountSurface`` renders instead
 * of the bare surface component. It:
 *
 *   - renders the chosen *primary* surface (chat / search / …),
 *   - on a ``BrowseLink`` click, keeps the primary surface mounted
 *     but hidden (``hidden`` attr → ``display:none``) so its React
 *     state survives — the chat conversation, the search results —
 *     and renders ``BrowseSurface`` on top with a "← Back"
 *     affordance,
 *   - integrates with the browser history: opening Browse pushes a
 *     history entry (URL fragment ``#galen-browse=<guidelineId>``,
 *     state ``{galenView:'browse',…}``); the browser Back button
 *     (or the in-widget Back button) pops it via ``popstate`` and
 *     the primary surface re-appears in the exact state it was
 *     left in,
 *   - on first mount, if the host page URL already carries the
 *     ``#galen-browse=…`` fragment (a copied / middle-clicked
 *     BrowseLink opened in a fresh tab) it opens Browse directly.
 *
 * Auth: the Browse viewer runs on the same host page with the same
 * widget session/token — no portal redirect, no token handoff.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { WidgetRuntime } from '../core/init';
import { resolveBrowseTocDepth, type WidgetSurface } from '../core/config';
import { assertSurfaceEnabled } from '../core/surfaces';

import { BrowseSurface } from './browse';
import { GalenChat } from './chat';
import { GalenSearch } from './galen-search';
import {
  WidgetNavigationContext,
  type WidgetNavigationValue,
} from './navigation-context';
import { SemanticSearch } from './semantic-search';
import { SimpleSearch } from './simple-search';

interface BrowseTarget {
  guidelineId: number;
  sectionId: number | null;
}

interface GalenHistoryState {
  galenView?: 'browse';
  galenHostId?: string;
  guidelineId?: number;
  sectionId?: number | null;
}

/** The URL fragment used to encode an in-place Browse deep-link. */
const BROWSE_HASH_RE = /^#galen-browse=(\d+)(?:\.(\d+))?$/;

function browseHash(guidelineId: number, sectionId: number | null): string {
  return (
    '#galen-browse=' +
    String(guidelineId) +
    (sectionId != null ? '.' + String(sectionId) : '')
  );
}

function pathWithoutHash(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Human-readable name for a surface id (`"semantic-search"` →
 * `"Semantic Search"`, `"galen-search"` → `"Galen Search"`,
 * `"chat"` → `"Chat"`). Used for the in-place-Browse back label so
 * it says exactly where it goes.
 */
function surfaceDisplayName(surface: string): string {
  return surface
    .split('-')
    .map((word) => (word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1)))
    .join(' ');
}

const SURFACE_COMPONENTS: Record<
  string,
  React.ComponentType<{
    client: WidgetRuntime['client'];
    baseUrl: string;
    token: string;
    onError?: (error: Error) => void;
    enableBrowseLinks?: boolean;
    /** AB#4401: only the Chat surface reads this; others ignore it. */
    assistantName?: string;
    /** GALEN-CHAT-V1-R02: only the Chat surface reads this. */
    safetyDisclaimer?: string;
    /** WIDGET-V1-R28: only the Browse surface reads this. */
    tocDepth?: number;
  }>
> = {
  chat: GalenChat as never,
  'galen-search': GalenSearch as never,
  'semantic-search': SemanticSearch as never,
  'simple-search': SimpleSearch as never,
  browse: BrowseSurface as never,
};

export interface SurfaceHostProps {
  runtime: WidgetRuntime;
  surface: string;
  onError?: (error: Error) => void;
}

export const SurfaceHost: React.FC<SurfaceHostProps> = ({
  runtime,
  surface,
  onError,
}) => {
  // Re-run the enabled-surfaces gate (mountSurface already did, but
  // a direct render of SurfaceHost should fail the same way).
  assertSurfaceEnabled(
    surface,
    runtime.config.config.enabled_surfaces as readonly WidgetSurface[],
  );

  const Primary = SURFACE_COMPONENTS[surface];
  if (Primary == null) {
    // `enabled_surfaces` listed a value the widget wasn't built
    // for (frontend/backend drift). mountSurface's gate normally
    // catches disabled surfaces before we get here.
    throw new Error(`Unknown widget surface: ${surface}`);
  }

  const [browse, setBrowse] = useState<BrowseTarget | null>(null);
  // Stable per-host id so a page that mounts the widget twice keeps
  // each host's history entries from toggling the other host.
  const hostId = useRef<string>(
    'gh' + Math.random().toString(36).slice(2),
  );
  // True once we've pushed at least one history entry, so the Back
  // button knows whether `history.back()` is safe (vs. clearing
  // state directly when we only ever did a replaceState on load).
  const pushedEntry = useRef(false);

  const baseUrl = runtime.client.getBaseUrl();
  const token = runtime.client.getToken();
  const enableBrowseLinks = runtime.config.enable_browse_links;
  // AB#4401: configurable Chat assistant display name; the Chat
  // surface falls back to "Galen" when the config omits it.
  const assistantName = runtime.config.config.assistant_name;
  // GALEN-CHAT-V1-R02: branding safety disclaimer surfaced on the
  // widget-config response; the Chat surface renders it below the
  // input (falling back to its built-in default when absent/null).
  const safetyDisclaimer = runtime.config.chat_safety_disclaimer ?? undefined;
  // WIDGET-V1-R28: per-deployment Browse TOC depth, resolved once
  // (absent/malformed -> 2) and handed to both Browse mounts below.
  const tocDepth = resolveBrowseTocDepth(runtime.config.config.browse_toc_depth);
  // AB#4404 / AB#4406: WidgetConfig.landing_content is *exposed* via
  // runtime.config.config.landing_content but NOT rendered by the
  // widget — host pages decide where/when to render it (so it sits
  // on the landing screen only, not above every mounted surface).

  const openBrowse = useCallback(
    (guidelineId: number, sectionId: number | null) => {
      const state: GalenHistoryState = {
        galenView: 'browse',
        galenHostId: hostId.current,
        guidelineId,
        sectionId,
      };
      try {
        window.history.pushState(
          state,
          '',
          pathWithoutHash() + browseHash(guidelineId, sectionId),
        );
        pushedEntry.current = true;
      } catch {
        // pushState can throw in some sandboxed iframes — fall back
        // to a history-less in-memory swap; Back won't restore the
        // surface but the Browse view still works.
      }
      setBrowse({ guidelineId, sectionId });
    },
    [],
  );

  const closeBrowse = useCallback(() => {
    if (pushedEntry.current) {
      // popstate handler clears `browse`.
      window.history.back();
    } else {
      setBrowse(null);
      try {
        window.history.replaceState(
          window.history.state,
          '',
          pathWithoutHash(),
        );
      } catch {
        /* ignore */
      }
    }
  }, []);

  // popstate is the source of truth for whether the Browse view is
  // shown: our state object decides. Any state that isn't ours (the
  // host page's, or the entry before our push) means "show primary".
  useEffect(() => {
    const onPop = (event: PopStateEvent): void => {
      const s = event.state as GalenHistoryState | null;
      if (
        s != null &&
        s.galenView === 'browse' &&
        s.galenHostId === hostId.current &&
        typeof s.guidelineId === 'number'
      ) {
        setBrowse({ guidelineId: s.guidelineId, sectionId: s.sectionId ?? null });
      } else {
        setBrowse(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // First-mount: if the host URL already carries #galen-browse=… (a
  // copied / middle-clicked BrowseLink opened fresh), open Browse.
  // Strip the fragment from the current entry first (preserving any
  // host state) then push our browse entry, so the browser Back
  // button returns to the host page without the fragment.
  useEffect(() => {
    const m = BROWSE_HASH_RE.exec(window.location.hash);
    if (m == null) return;
    const guidelineId = Number(m[1]);
    const sectionId = m[2] != null ? Number(m[2]) : null;
    if (!Number.isFinite(guidelineId)) return;
    try {
      window.history.replaceState(window.history.state, '', pathWithoutHash());
    } catch {
      /* ignore */
    }
    openBrowse(guidelineId, sectionId);
    // openBrowse already setBrowse + pushedEntry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navValue = useMemo<WidgetNavigationValue>(
    () => ({ openBrowse }),
    [openBrowse],
  );

  return (
    <WidgetNavigationContext.Provider value={navValue}>
      <div data-galen-surface-host>
        <div data-galen-surface-host-primary hidden={browse != null}>
          <Primary
            client={runtime.client}
            baseUrl={baseUrl}
            token={token}
            onError={onError}
            enableBrowseLinks={enableBrowseLinks}
            assistantName={assistantName}
            safetyDisclaimer={safetyDisclaimer}
            tocDepth={tocDepth}
          />
        </div>
        {browse != null && (
          <div data-galen-surface-host-browse>
            <div
              data-galen-surface-backbar
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.5rem 0.75rem',
                borderBottom: '1px solid var(--galen-border, #e2e8f0)',
              }}
            >
              <button
                type="button"
                data-galen-surface-back
                onClick={closeBrowse}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: 'var(--galen-primary, hsl(199, 89%, 48%))',
                  padding: '0.25rem 0.4rem',
                }}
              >
                <span aria-hidden="true">&larr;</span> Back to{' '}
                {surfaceDisplayName(surface)}
              </button>
            </div>
            <BrowseSurface
              client={runtime.client}
              baseUrl={baseUrl}
              token={token}
              onError={onError}
              initialGuidelineId={browse.guidelineId}
              initialSectionId={browse.sectionId ?? undefined}
              tocDepth={tocDepth}
            />
          </div>
        )}
      </div>
    </WidgetNavigationContext.Provider>
  );
};
